import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import StyledDialog from './styles/StyledDialog';
import RightButton from './styles/RightButton';
import CellWrapper from './styles/CellWrapper';
import EditableField from '../../../../../../shared/ui/EditableField/EditableField';
import { getFactTotalFromElevator, getFactTotalFromField } from '../../../utils';

function SolutionMatrixDialog({ open, onClose, fields, elevators, solutionData, onChangeFactVolumes }) {
  const {
    date,
    planMatrix,
    factMatrix,
    factTotalFromFields,
    planTotalFromFields,
    factTotalFromElevators,
    planTotalFromElevators,
  } = solutionData;

  const [localFactMatrix, setLocalFactMatrix] = useState({ ...factMatrix });
  const [localFactTotalFromFields, setLocalFactTotalFromFields] = useState({ ...factTotalFromFields });
  const [localFactTotalFromElevators, setLocalFactTotalFromElevators] = useState({ ...factTotalFromElevators });

  useEffect(() => {
    setLocalFactMatrix({ ...factMatrix });
    setLocalFactTotalFromFields({ ...factTotalFromFields });
    setLocalFactTotalFromElevators({ ...factTotalFromElevators });
  }, [solutionData]);

  const handleChageFactCell = (fieldId, elevatorId, newValue) => {
    if (Number.isNaN(Number(newValue))) return;

    const currentValue = newValue.trim() === '' ? null : Number(newValue);

    setLocalFactMatrix((prev) => {
      const updatedMatrix = { ...prev };
      const current = updatedMatrix[fieldId] ? [...updatedMatrix[fieldId]] : [];

      const idx = current.findIndex((c) => c.consumerUnloadingGeoZoneIdTo === elevatorId);

      if (idx >= 0) {
        current[idx] = {
          ...current[idx],
          factExportVolume: currentValue,
        };
      } else {
        current.push({
          consumerUnloadingGeoZoneIdTo: elevatorId,
          factExportVolume: currentValue,
        });
      }

      updatedMatrix[fieldId] = current;

      // пересчитываем суммы по полям и элеваторам
      const recalculatedFields = {};
      fields.forEach((field) => {
        const valueFromCells = getFactTotalFromField(updatedMatrix, field.id);
        if (!localFactTotalFromFields?.[field.id] && valueFromCells === 0) return;

        recalculatedFields[field.id] = valueFromCells;
      });

      const recalculatedElevators = {};
      elevators.forEach((el) => {
        const valueFromCells = getFactTotalFromElevator(updatedMatrix, el.id);
        if (!localFactTotalFromElevators?.[el.id] && valueFromCells === 0) return;

        recalculatedElevators[el.id] = valueFromCells;
      });

      setLocalFactTotalFromFields(recalculatedFields);
      setLocalFactTotalFromElevators(recalculatedElevators);

      return updatedMatrix;
    });
  };

  const handleChangeFactTotalFromField = (fieldId, newValue) => {
    if (Number.isNaN(Number(newValue))) return;

    const currentValue = newValue.trim() === '' ? null : Number(newValue);

    setLocalFactTotalFromFields((prev) => {
      const updated = { ...prev, [fieldId]: currentValue };
      return updated;
    });
  };

  const handleChangeFactTotalFromElevator = (elevatorId, newValue) => {
    if (Number.isNaN(Number(newValue))) return;

    const currentValue = newValue.trim() === '' ? null : Number(newValue);

    setLocalFactTotalFromElevators((prev) => {
      const updated = { ...prev, [elevatorId]: currentValue };
      return updated;
    });
  };

  const handleClose = () => {
    onChangeFactVolumes(date, {
      exportVolume: localFactMatrix,
      avgExportFromField: localFactTotalFromFields,
      avgExportOnConsumer: localFactTotalFromElevators,
    });

    onClose();
  };

  if (!open) return null;

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="100%">
      <DialogTitle>Объемы перевозок (план/факт) на {date ? format(date, 'dd.MM.yyyy') : ''}</DialogTitle>
      <DialogContent>
        <Table sx={{ minWidth: '800px' }}>
          <TableHead>
            <TableRow>
              <TableCell />
              {elevators.map((el) => (
                <TableCell key={el.id} align="center">
                  {el.name}
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((field) => (
              <TableRow key={field.id}>
                <TableCell>{field.name}</TableCell>
                {elevators.map((el) => {
                  const planCell = planMatrix?.find(
                    (m) => m.fieldGeoZoneIdFrom === field.id && m.consumerUnloadingGeoZoneIdTo === el.id,
                  );
                  const factCell = localFactMatrix?.[field.id]?.find((c) => c.consumerUnloadingGeoZoneIdTo === el.id);
                  return (
                    <TableCell key={el.id} align="center">
                      <CellWrapper>
                        <Typography textAlign="right">{planCell?.volume || '-'}</Typography>
                        <Typography> / </Typography>
                        <EditableField
                          value={factCell?.factExportVolume}
                          onChange={(newValue) => handleChageFactCell(field.id, el.id, newValue)}
                        />
                      </CellWrapper>
                    </TableCell>
                  );
                })}
                <TableCell align="center">
                  <CellWrapper>
                    <Typography textAlign="right">{planTotalFromFields?.[field.id] || '-'}</Typography>
                    <Typography> / </Typography>
                    <EditableField
                      value={localFactTotalFromFields?.[field.id]}
                      extraLabel={getFactTotalFromField(localFactMatrix, field.id)}
                      onChange={(newValue) => handleChangeFactTotalFromField(field.id, newValue)}
                      width={60}
                    />
                  </CellWrapper>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell />
              {elevators.map((el) => (
                <TableCell key={el.id} align="center">
                  <CellWrapper>
                    <Typography textAlign="right">{planTotalFromElevators?.[el.id] || '-'}</Typography>
                    <Typography> / </Typography>
                    <EditableField
                      value={localFactTotalFromElevators?.[el.id]}
                      extraLabel={getFactTotalFromElevator(localFactMatrix, el.id)}
                      onChange={(newValue) => handleChangeFactTotalFromElevator(el.id, newValue)}
                    />
                  </CellWrapper>
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </DialogContent>
      <RightButton onClick={handleClose}>Ок</RightButton>
    </StyledDialog>
  );
}

SolutionMatrixDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fields: PropTypes.arrayOf(PropTypes.string),
  elevators: PropTypes.arrayOf(PropTypes.string),
  onChangeFactVolumes: PropTypes.func.isRequired,
  solutionData: PropTypes.shape(),
};

SolutionMatrixDialog.defaultProps = {
  fields: [],
  elevators: [],
  solutionData: {},
};

export default SolutionMatrixDialog;
